'use client';

/**
 * Spanish review / hand-polish panel for the admin content forms (MWBI-900).
 *
 * Shared by CreateProgramCardModal, CreateResourceModal, CreateBlogModal and the blog
 * detail edit form, so all four surfaces behave identically rather than drifting apart.
 *
 * Two constraints drive the design, both from how this client actually works:
 *
 *  1. ~90% of the authors read and write only English. An editable Spanish box next to the
 *     English one is a trap: typing in it latches the item to manual for good, and nobody
 *     on the team could read the result well enough to notice it had gone stale. So the
 *     Spanish is READ-ONLY until the admin deliberately unlocks it, and the consequence is
 *     spelled out before they do.
 *
 *  2. Nobody has access to the Strapi admin panel, so this is the ONLY place a failed or
 *     hand-edited translation is visible. Hence the status badge.
 *
 * Dirty-tracking is the other half of the contract, and it lives here: `onChange` fires
 * only for a field the admin actually edited. A form that echoed back untouched Spanish
 * would diverge the stored fingerprint and false-lock the item — the sharpest failure mode
 * in this feature, because it is silent.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Pencil } from 'lucide-react';

import { useLanguage } from '@/context/LanguageContext';
import type { TranslationStatus } from '@/types/translation';

export interface TranslationPanelField {
  /** Matches the CMS column, e.g. 'titleEs'. */
  name: string;
  label: string;
  value: string;
  multiline?: boolean;
}

interface TranslationPanelProps {
  fields: TranslationPanelField[];
  status?: TranslationStatus | null;
  locked?: boolean | null;
  /** Fired only when the admin actually edits a Spanish field. */
  onChange: (name: string, value: string) => void;
  /** Fired when the admin asks for automatic translation to resume. */
  onResetToAutomatic: () => void;
  /** Parent visibility. State produced here is cleared whenever this goes false. */
  isActive: boolean;
  disabled?: boolean;
  testIdPrefix?: string;
}

type Tone = 'ok' | 'manual' | 'warn' | 'pending';

export function TranslationPanel({
  fields,
  status,
  locked,
  onChange,
  onResetToAutomatic,
  isActive,
  disabled = false,
  testIdPrefix = 'translation-panel',
}: TranslationPanelProps) {
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);

  /**
   * Reset state this component PRODUCED when the surface goes inactive.
   *
   * Every parent renders its modal as <Modal isOpen={...} />, so the component stays
   * mounted while hidden and its hooks keep running. Gating the inputs is not enough: if
   * an admin unlocked Spanish on item A, closed the modal and reopened it on item B, the
   * unlocked state would carry over and B's Spanish would be editable — and one keystroke
   * would silently latch B to manual.
   */
  useEffect(() => {
    if (!isActive) setIsEditing(false);
  }, [isActive]);

  const handleEdit = useCallback(() => setIsEditing(true), []);
  const handleCancelEdit = useCallback(() => setIsEditing(false), []);

  const handleReset = useCallback(() => {
    setIsEditing(false);
    onResetToAutomatic();
  }, [onResetToAutomatic]);

  if (!isActive) return null;

  const isLocked = locked === true;
  const hasAnySpanish = fields.some((field) => field.value.trim() !== '');

  const { badge, explanation, tone } = describeState(t, { status, isLocked, hasAnySpanish });
  const editable = isEditing && !disabled;
  const explanationId = `${testIdPrefix}-explanation`;

  return (
    <section
      data-testid={testIdPrefix}
      className="rounded-md border border-border p-3 space-y-3"
      aria-labelledby={`${testIdPrefix}-heading`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 id={`${testIdPrefix}-heading`} className="text-sm font-medium text-foreground">
          {t.translation.sectionLabel}
        </h3>
        <span
          data-testid={`${testIdPrefix}-badge`}
          data-tone={tone}
          className={`inline-flex items-center gap-1 text-xs font-medium ${TONE_CLASS[tone]}`}
        >
          <ToneIcon tone={tone} />
          {badge}
        </span>
      </div>

      <p
        id={explanationId}
        data-testid={`${testIdPrefix}-explanation`}
        // role="alert" only for states that need attention, so routine renders are silent.
        role={tone === 'warn' ? 'alert' : undefined}
        className="text-xs text-foreground-muted"
      >
        {explanation}
      </p>

      <div className="space-y-2">
        {fields.map((field) => (
          <div key={field.name}>
            <label
              className="block text-xs font-medium text-foreground-muted mb-1"
              htmlFor={`${testIdPrefix}-${field.name}`}
            >
              {field.label}
            </label>
            {editable ? (
              renderInput(field, testIdPrefix, explanationId, onChange)
            ) : (
              <p
                id={`${testIdPrefix}-${field.name}`}
                data-testid={`${testIdPrefix}-${field.name}-readonly`}
                lang="es"
                className="text-sm text-foreground whitespace-pre-wrap break-words"
              >
                {field.value.trim() === '' ? t.translation.emptyValue : field.value}
              </p>
            )}
          </div>
        ))}
      </div>

      {isEditing && (
        <p
          data-testid={`${testIdPrefix}-edit-warning`}
          role="alert"
          className="text-xs text-amber-700"
        >
          {t.translation.editWarning}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {!isEditing ? (
          <button
            type="button"
            data-testid={`${testIdPrefix}-edit-btn`}
            onClick={handleEdit}
            disabled={disabled}
            aria-describedby={explanationId}
            className={PANEL_BUTTON_CLASS}
          >
            {t.translation.editSpanish}
          </button>
        ) : (
          <button
            type="button"
            data-testid={`${testIdPrefix}-cancel-edit-btn`}
            onClick={handleCancelEdit}
            disabled={disabled}
            className={PANEL_BUTTON_CLASS}
          >
            {t.translation.cancelEditSpanish}
          </button>
        )}

        {(isLocked || hasAnySpanish) && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-reset-btn`}
            onClick={handleReset}
            disabled={disabled}
            aria-describedby={`${testIdPrefix}-reset-hint`}
            className={PANEL_BUTTON_CLASS}
          >
            {t.translation.resetToAutomatic}
          </button>
        )}
      </div>

      {(isLocked || hasAnySpanish) && (
        <p
          id={`${testIdPrefix}-reset-hint`}
          data-testid={`${testIdPrefix}-reset-hint`}
          className="text-xs text-foreground-muted"
        >
          {t.translation.resetHint}
        </p>
      )}
    </section>
  );
}

/**
 * Shared treatment for the panel's two controls, so they read as buttons rather than as
 * faint outlines against the panel.
 *
 * The hover state uses `border-dark` rather than `border`: `--border` is redefined to
 * pure black under `prefers-contrast: high` (globals.css), which would turn the button
 * black-on-dark-text for exactly the users who need contrast most. `--border-dark` is not
 * overridden there.
 */
const PANEL_BUTTON_CLASS =
  'px-3 py-1.5 text-xs font-medium text-foreground bg-sidebar-hover border border-border-dark '
  + 'rounded-md hover:bg-border-dark transition '
  + 'disabled:opacity-50 disabled:cursor-not-allowed';

const TONE_CLASS: Record<Tone, string> = {
  ok: 'text-green-700',
  manual: 'text-foreground-muted',
  warn: 'text-amber-700',
  pending: 'text-foreground-muted',
};

function ToneIcon({ tone }: { tone: Tone }) {
  if (tone === 'ok') return <CheckCircle2 size={14} aria-hidden="true" />;
  if (tone === 'manual') return <Pencil size={14} aria-hidden="true" />;
  if (tone === 'pending') return <Loader2 size={14} aria-hidden="true" />;
  return <AlertTriangle size={14} aria-hidden="true" />;
}

function renderInput(
  field: TranslationPanelField,
  testIdPrefix: string,
  describedBy: string,
  onChange: (name: string, value: string) => void,
) {
  const shared = {
    id: `${testIdPrefix}-${field.name}`,
    'data-testid': `${testIdPrefix}-${field.name}-input`,
    lang: 'es',
    value: field.value,
    'aria-describedby': describedBy,
    // Dirty-tracking: the parent records this field as edited only when it fires.
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(field.name, event.target.value),
    className:
      'w-full px-3 py-2 border border-border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary',
  };

  return field.multiline ? (
    <textarea {...shared} rows={field.name === 'contentEs' ? 10 : 3} />
  ) : (
    <input type="text" {...shared} />
  );
}

function describeState(
  t: ReturnType<typeof useLanguage>['t'],
  state: { status?: TranslationStatus | null; isLocked: boolean; hasAnySpanish: boolean },
): { badge: string; explanation: string; tone: Tone } {
  if (state.isLocked) {
    return { badge: t.translation.statusManual, explanation: t.translation.explainManual, tone: 'manual' };
  }
  if (state.status === 'failed') {
    return {
      badge: t.translation.statusUnavailable,
      explanation: t.translation.explainUnavailable,
      tone: 'warn',
    };
  }
  if (state.status === 'partial') {
    return { badge: t.translation.statusPartial, explanation: t.translation.explainPartial, tone: 'warn' };
  }
  if (state.status === 'ok' || state.hasAnySpanish) {
    return { badge: t.translation.statusAuto, explanation: t.translation.explainAuto, tone: 'ok' };
  }
  return { badge: t.translation.statusPending, explanation: t.translation.explainPending, tone: 'pending' };
}
